import { IpqsEmailDetail, IpqsEntryResponse, IpqsPhoneDetail } from '@src/data/types/FraudAnalysisResult'
import { getApiClient } from '@src/services/api-client'
import { FullCreditApplicationDto } from '../full-credit-application/FullCreditApplicationDto'

export interface FraudFlagDto {
  key: string;
  displayNameEn: string;
  displayNameFr: string;
  category: string;
  isAutomatic: boolean;
  value: boolean;
  lastModified?: string;
  modifiedBy?: string;
  modifiedByName?: string;
  reason?: string;
  documentIds?: string[]
}

export interface FraudFlagsResponse {
  creditApplicationId: string;
  financingProgramId: string;
  flags: Record<string, FraudFlagDto[]>
}

export interface UpdateFlagRequest {
  value: boolean;
  reason?: string;
  documentIds?: string[]
}

export interface FraudFlagChangeDto {
  flagName: string;
  oldValue: boolean;
  newValue: boolean;
  changeDate: string;
  changedBy: string;
  changedByName: string;
  reason?: string;
  documentIds?: string[]
}

class FraudApi {
  private fraudUrl(financingProgramId: string, creditApplicationId: string) {
    return `/Applications/${encodeURIComponent(financingProgramId)}/${encodeURIComponent(creditApplicationId)}/Fraud`;
  }

  async getFraudFlags(financingProgramId: string, creditApplicationId: string): Promise<FraudFlagsResponse> {
    const apiClient = getApiClient();
    const response = await apiClient.get<FraudFlagsResponse>(
      this.fraudUrl(financingProgramId, creditApplicationId)
    );
    return response.data;
  }

  async updateFlag(
    financingProgramId: string,
    creditApplicationId: string,
    indicatorType: string,
    flagKey: string,
    request: UpdateFlagRequest
  ): Promise<void> {
    const apiClient = getApiClient();
    await apiClient.put(
      `${this.fraudUrl(financingProgramId, creditApplicationId)}/indicator/${indicatorType}/${flagKey}`,
      request
    );
  }

  async getFlagHistory(financingProgramId: string, creditApplicationId: string): Promise<FraudFlagChangeDto[]> {
    const apiClient = getApiClient();
    const response = await apiClient.get<FraudFlagChangeDto[]>(
      `${this.fraudUrl(financingProgramId, creditApplicationId)}/history`
    );
    return response.data;
  }

  async calculateAutomaticFlags(financingProgramId: string, creditApplicationId: string): Promise<void> {
    const apiClient = getApiClient();
    await apiClient.post(
      `${this.fraudUrl(financingProgramId, creditApplicationId)}/calculate`
    );
  }

  async launchFraudIQAnalysis(financingProgramId: string, creditApplicationId: string): Promise<FullCreditApplicationDto> {
    const apiClient = getApiClient();
    const response = await apiClient.post<FullCreditApplicationDto>(
      `${this.fraudUrl(financingProgramId, creditApplicationId)}/launchFraudIQ`
    );
    return response.data;
  }
  async getIpqsEmailById(id: string): Promise<IpqsEntryResponse<IpqsEmailDetail>> {
    const apiClient = getApiClient()
    const response = await apiClient.get<IpqsEntryResponse<IpqsEmailDetail>>(`/Applications/Ipqs/Email/${encodeURIComponent(id)}`)
    return response.data
  }

  async getIpqsPhoneById(id: string): Promise<IpqsEntryResponse<IpqsPhoneDetail>> {
    const apiClient = getApiClient()
    const response = await apiClient.get<IpqsEntryResponse<IpqsPhoneDetail>>(`/Applications/Ipqs/Phone/${encodeURIComponent(id)}`)
    return response.data
  }
}

export const fraudApi = new FraudApi();
